import type { AudioEngine } from './engine';

/**
 * Creature voice synth. Each creature's voice ("voiceprint") is derived
 * deterministically from its numeric seed — same creature, same voice,
 * every session. All pitches snap to A major pentatonic so any number of
 * overlapping creatures harmonize instead of clashing.
 *
 * Per-syllable voice: 2-osc FM (triangle carrier, sine modulator at ~2:1
 * with a per-creature detune), modulation index enveloped 3 -> 0.5, amp AD
 * envelope, all through a shared 800-4000 Hz formant bandpass to voiceBus.
 */

/** A major pentatonic semitone offsets from A (A B C# E F#). */
const DEGREES = [0, 2, 4, 7, 9] as const;
const BASE_HZ = 440; // A4

/** Frequency for a pentatonic scale index (5 degrees per octave, A4 = 0). */
function pentatonicHz(index: number): number {
  const octave = Math.floor(index / 5);
  const degree = DEGREES[((index % 5) + 5) % 5] ?? 0;
  return BASE_HZ * Math.pow(2, (octave * 12 + degree) / 12);
}

/** splitmix32-style stream over the bits of an integer seed. */
function splitmix32(seed: number): () => number {
  let s = seed | 0;
  return () => {
    s = (s + 0x9e3779b9) | 0;
    let z = s;
    z = Math.imul(z ^ (z >>> 16), 0x21f0aaad);
    z = Math.imul(z ^ (z >>> 15), 0x735a2d97);
    z ^= z >>> 15;
    return z >>> 0;
  };
}

const clamp01 = (v: number): number => Math.min(1, Math.max(0, v));

interface Voiceprint {
  /** Scale index of the creature's home pitch (0..5 -> 440-880 Hz). */
  baseIndex: number;
  /** Tendency for phrases to rise (+1) or fall (-1). */
  contourBias: number;
  /** Per-creature FM ratio detune, +-0.03 around 2.0. */
  ratioDetune: number;
  /** The creature's "name": three scale-degree steps from its home pitch. */
  motif: [number, number, number];
}

const MAX_VOICES = 8;

export class ChirpSynth {
  private readonly engine: AudioEngine;
  private readonly voiceprints = new Map<number, Voiceprint>();
  private formantFilter: BiquadFilterNode | null = null;
  private activeVoices = 0;
  private chirpCounter = 0;

  constructor(engine: AudioEngine) {
    this.engine = engine;
  }

  private get ready(): boolean {
    return this.engine.context.state === 'running';
  }

  /** Shared 800-4000 Hz bandpass all syllables speak through, lazily built. */
  private formant(): BiquadFilterNode {
    if (!this.formantFilter) {
      const ctx = this.engine.context;
      const filter = ctx.createBiquadFilter();
      filter.type = 'bandpass';
      filter.frequency.value = Math.sqrt(800 * 4000); // geometric center ~1789 Hz
      filter.Q.value = 0.56; // ~800-4000 Hz band
      filter.connect(this.engine.voiceBus);
      this.formantFilter = filter;
    }
    return this.formantFilter;
  }

  private voiceprint(seed: number): Voiceprint {
    let vp = this.voiceprints.get(seed);
    if (vp) return vp;
    const rand = splitmix32(seed);
    const step = (): number => {
      const r = rand() % 4; // nonzero step in {-2,-1,1,2} so motifs always move
      return r < 2 ? r - 2 : r - 1;
    };
    vp = {
      baseIndex: rand() % 6, // pentatonic indices 0..5 span 440-880 Hz
      contourBias: (rand() / 0xffffffff) * 2 - 1,
      ratioDetune: ((rand() / 0xffffffff) * 2 - 1) * 0.03,
      motif: [step(), step(), step()],
    };
    this.voiceprints.set(seed, vp);
    return vp;
  }

  /**
   * One FM syllable at time t. attack 8 ms; decay/peak come from mood.
   * Silently dropped when the polyphony cap is hit.
   */
  private syllable(t: number, scaleIndex: number, decay: number, peak: number, vp: Voiceprint): void {
    if (this.activeVoices >= MAX_VOICES) return;
    const ctx = this.engine.context;
    const freq = pentatonicHz(Math.min(15, Math.max(-5, scaleIndex)));
    const attack = 0.008;
    const end = t + attack + decay;

    const carrier = ctx.createOscillator();
    carrier.type = 'triangle';
    // pitch identity is seeded; this hair of detune is transient decoration
    carrier.frequency.value = freq * (1 + (Math.random() - 0.5) * 0.006);

    const modulator = ctx.createOscillator();
    modulator.type = 'sine';
    const modFreq = freq * 2 * (1 + vp.ratioDetune);
    modulator.frequency.value = modFreq;

    // modulation index 3 -> 0.5 over the syllable (deviation = index * modFreq)
    const modDepth = ctx.createGain();
    modDepth.gain.setValueAtTime(3 * modFreq, t);
    modDepth.gain.exponentialRampToValueAtTime(0.5 * modFreq, end);
    modulator.connect(modDepth);
    modDepth.connect(carrier.frequency);

    const amp = ctx.createGain();
    amp.gain.setValueAtTime(0.0001, t);
    amp.gain.linearRampToValueAtTime(peak, t + attack);
    amp.gain.exponentialRampToValueAtTime(0.0001, end);
    carrier.connect(amp);
    amp.connect(this.formant());

    this.activeVoices++;
    carrier.onended = () => {
      this.activeVoices--;
    };
    carrier.start(t);
    modulator.start(t);
    carrier.stop(end + 0.02);
    modulator.stop(end + 0.02);
  }

  /**
   * A short vocalization. mood 0..1 (default 0.8): happy is higher, rising,
   * quicker; sad is lower, falling, slower, quieter. Syllable count defaults
   * to 1-3, drawn deterministically from the seed plus an internal counter,
   * so repeat calls vary while the voice itself stays stable.
   */
  chirp(voiceSeed: number, opts?: { mood?: number; syllables?: number }): void {
    if (!this.ready) return;
    const vp = this.voiceprint(voiceSeed);
    const mood = clamp01(opts?.mood ?? 0.8);
    const rand = splitmix32((voiceSeed | 0) ^ Math.imul(this.chirpCounter++, 0x9e3779b9));
    const count = opts?.syllables ?? 1 + (rand() % 3);

    const registerShift = Math.round((mood - 0.5) * 4); // -2..+2 scale steps
    const contour = vp.contourBias * 0.7 + (mood - 0.5) * 1.8;
    const decay = 0.06 + (1 - mood) * 0.12; // 60-180 ms
    const gap = 0.03 + (1 - mood) * 0.06;
    const peak = 0.09 + 0.09 * mood;

    const t0 = this.engine.context.currentTime;
    for (let i = 0; i < count; i++) {
      const jitter = (rand() % 3) - 1; // -1..+1 scale steps
      const index = vp.baseIndex + registerShift + Math.round(contour * i) + jitter;
      this.syllable(t0 + i * (0.008 + decay * 0.8 + gap), index, decay, peak, vp);
    }
  }

  /** Sing the creature's stable 3-note motif — its "name". */
  motif(voiceSeed: number): void {
    if (!this.ready) return;
    const vp = this.voiceprint(voiceSeed);
    const t0 = this.engine.context.currentTime;
    let index = vp.baseIndex;
    vp.motif.forEach((step, i) => {
      index += step;
      this.syllable(t0 + i * 0.16, index, 0.11, 0.16, vp);
    });
  }

  /** Low quiet warble: 6 Hz LFO over a triangle two octaves below home pitch. */
  purr(voiceSeed: number, durationSec: number): void {
    if (!this.ready) return;
    if (this.activeVoices >= MAX_VOICES) return;
    const ctx = this.engine.context;
    const vp = this.voiceprint(voiceSeed);
    const dur = Math.min(8, Math.max(0.2, durationSec));
    const t0 = ctx.currentTime;
    const end = t0 + dur;

    const osc = ctx.createOscillator();
    osc.type = 'triangle';
    osc.frequency.value = pentatonicHz(vp.baseIndex - 10); // 110-220 Hz

    const lfo = ctx.createOscillator();
    lfo.type = 'sine';
    lfo.frequency.value = 6;

    // LFO wobbles both loudness and pitch a touch — a contented rumble
    const tremolo = ctx.createGain();
    tremolo.gain.setValueAtTime(0.03, t0);
    tremolo.gain.setValueAtTime(0.03, Math.max(t0, end - 0.2));
    tremolo.gain.linearRampToValueAtTime(0, end);
    const vibrato = ctx.createGain();
    vibrato.gain.value = 4;
    lfo.connect(tremolo);
    lfo.connect(vibrato);
    vibrato.connect(osc.frequency);

    const amp = ctx.createGain();
    amp.gain.setValueAtTime(0.0001, t0);
    amp.gain.linearRampToValueAtTime(0.07, t0 + 0.15);
    amp.gain.setValueAtTime(0.07, Math.max(t0 + 0.15, end - 0.2));
    amp.gain.exponentialRampToValueAtTime(0.0001, end);
    tremolo.connect(amp.gain);
    osc.connect(amp);
    amp.connect(this.engine.voiceBus);

    this.activeVoices++;
    osc.onended = () => {
      this.activeVoices--;
    };
    osc.start(t0);
    lfo.start(t0);
    osc.stop(end + 0.02);
    lfo.stop(end + 0.02);
  }
}
